import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ListChecks, MessageCircle, HeartPulse, Target, BookOpen, ShieldCheck,
  Check, ArrowRight, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { KEYWORKER_FEATURES, KEYWORKER_BENEFITS, KEYWORKER_ROADMAP, IMAGES, KEYWORKER } from "@/lib/data";

const ICONS = { ListChecks, MessageCircle, HeartPulse, Target, BookOpen, ShieldCheck };

const PocketKeyworker = () => (
  <div data-testid="pocket-keyworker-page">
    {/* Hero */}
    <section className="relative overflow-hidden bg-navy text-cream grain pt-40 pb-28">
      <div className="relative mx-auto max-w-7xl px-6 grid gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <p className="overline mb-6">Digital Innovation</p>
          <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream">
            Pocket <span className="italic text-gold">Keyworker</span>
          </h1>
          <p className="mt-6 font-serif italic text-xl text-gold">{KEYWORKER.tagline}</p>
          <p className="mt-6 max-w-md text-lg text-cream/80 leading-relaxed">
            A digital companion helping young people (aged 13–18) build life skills, wellbeing and
            independence — and helping schools, services and families support them meaningfully, every day.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button asChild className="rounded-full bg-gold text-navy hover:bg-gold-bright px-8 h-12">
              <Link to="/contact" data-testid="keyworker-partner-cta">
                Partner With Us <ArrowRight size={16} className="ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-cream/30 text-cream hover:bg-cream hover:text-navy px-8 h-12"
            >
              <a href={KEYWORKER.url} target="_blank" rel="noreferrer" data-testid="keyworker-visit-cta">
                Visit pocketkeyworker.app <ExternalLink size={15} className="ml-2" />
              </a>
            </Button>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative">
            {/* Spotlight */}
            <div className="absolute -inset-6 bg-[radial-gradient(circle_at_center,rgba(197,168,128,0.35),transparent_65%)] blur-2xl" />
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-[2rem] border-4 border-white/10 soft-shadow"
            >
              <img
                src={IMAGES.keyworker}
                alt="A young person using the Pocket Keyworker app"
                className="h-[480px] w-full object-cover"
              />
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Vision */}
    <section className="mx-auto max-w-4xl px-6 py-28 text-center">
      <Reveal>
        <p className="overline mb-5">The Vision</p>
        <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-navy leading-tight">
          Every young person deserves a trusted companion on the journey to independence.
        </h2>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Pocket Keyworker reimagines how care-experienced young people are supported — blending
          practical life skills, wellbeing and human connection into one accessible, safe platform.
        </p>
      </Reveal>
    </section>

    {/* Features */}
    <section className="bg-white border-y border-slate-200/70">
      <div className="mx-auto max-w-7xl px-6 py-28">
        <Reveal className="mb-16">
          <p className="overline mb-4">Key Features</p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-navy">
            Designed with purpose
          </h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {KEYWORKER_FEATURES.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <Reveal key={f.title} delay={i * 0.05}>
                <div
                  data-testid={`keyworker-feature-${i}`}
                  className="h-full rounded-3xl border border-slate-200 bg-cream p-8 transition-colors duration-300 hover:border-gold/60"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-teal text-cream">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-xl text-navy">{f.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    {/* Benefits */}
    <section className="mx-auto max-w-7xl px-6 py-28 grid gap-14 lg:grid-cols-2 lg:items-center">
      <Reveal>
        <p className="overline mb-4">Benefits</p>
        <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight">
          Meaningful support, measurable impact.
        </h2>
        <ul className="mt-8 space-y-4">
          {KEYWORKER_BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-3 text-lg text-navy/85">
              <Check size={20} className="mt-1 shrink-0 text-gold" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white soft-shadow">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
            <span className="h-3 w-3 rounded-full bg-red-400" />
            <span className="h-3 w-3 rounded-full bg-yellow-400" />
            <span className="h-3 w-3 rounded-full bg-green-400" />
            <span className="ml-3 text-xs text-slate-400">pocketkeyworker.app</span>
          </div>
          <img
            src={IMAGES.keyworkerApp}
            alt="Inside the Pocket Keyworker student portal"
            className="w-full object-cover object-top"
          />
        </div>
      </Reveal>
    </section>

    {/* Roadmap */}
    <section className="bg-navy text-cream grain">
      <div className="relative mx-auto max-w-5xl px-6 py-28">
        <Reveal className="mb-16">
          <p className="overline mb-4">Future Roadmap</p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-cream">
            The road ahead
          </h2>
        </Reveal>
        <div className="relative border-l border-white/15 pl-10 space-y-12">
          {KEYWORKER_ROADMAP.map((r, i) => (
            <Reveal key={r.phase} delay={i * 0.08}>
              <div data-testid={`roadmap-${i}`} className="relative">
                <span className="absolute -left-[3.15rem] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-gold text-navy text-xs font-bold">
                  {i + 1}
                </span>
                <p className="text-xs tracking-[0.2em] uppercase text-gold">{r.phase}</p>
                <h3 className="mt-2 font-serif text-2xl text-cream">{r.title}</h3>
                <p className="mt-2 text-cream/70 leading-relaxed max-w-lg">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default PocketKeyworker;
